import { useEffect } from 'react';

export default function useScrollReveal(selector = '[data-reveal]') {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll(selector));
    const parallaxNodes = Array.from(document.querySelectorAll('[data-parallax]'));
    if (!nodes.length && !parallaxNodes.length) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      parallaxNodes.forEach((node) => {
        node.style.transform = 'none';
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    nodes.forEach((node, index) => {
      node.style.setProperty('--reveal-delay', `${Math.min(index % 5, 4) * 65}ms`);
      observer.observe(node);
    });

    let ticking = false;
    const updateParallax = () => {
      const viewportCenter = window.innerHeight / 2;
      parallaxNodes.forEach((node) => {
        const speed = Number(node.getAttribute('data-parallax') || '0.06');
        const rect = node.getBoundingClientRect();
        const distance = viewportCenter - (rect.top + rect.height / 2);
        const y = distance * speed;
        node.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      });
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [selector]);
}
